<template>
  <div>
    <dl :qaid="`${qaidLabel}`">
      <div
        v-for="(spec, key) in specsToHighlight"
        :key="key"
        :class="['py-25 block', listItemClasses]"
        :qaid="`${qaidLabel}-${key}`"
      >
        <dt class="spec-name font-normal text-xs text-nowrap inline m-0 p-0" :qaid="`${qaidLabel}-${key}-name`">
          {{ $t(`product.specification.${key}`) }}
        </dt>
        <dd class="font-bold text-xs inline m-0 p-0" :qaid="`${qaidLabel}-${key}-value`" v-html="spec" />
      </div>
    </dl>
    <pdl-collapse
      v-if="isShowingAllSpecs && hasNonHighlightedSpecs"
      ref="specs-collapse"
      :default-active="false"
      @click="collapseChange"
    >
      <pdl-collapse-item name="model-specs" :title="specsTitle">
        <dl>
          <div
            v-for="(spec, key) in nonHighlightedSpecs"
            :key="key"
            :class="['py-25 block', listItemClasses]"
            :qaid="`${qaidLabel}-${key}`"
          >
            <dt class="spec-name font-normal text-xs inline m-0 p-0 text-nowrap" :qaid="`${qaidLabel}-${key}-name`">
              {{ $t(`product.specification.${key}`) }}
            </dt>
            <dd class="font-bold text-xs inline m-0 p-0" :qaid="`${qaidLabel}-${key}-value`" v-html="spec" />
          </div>
        </dl>
      </pdl-collapse-item>
    </pdl-collapse>
  </div>
</template>

<script>
import {mapGetters, mapState, mapMutations} from 'vuex';
import {PdlCollapse, PdlCollapseItem} from '@pedal/pdl-collapse';

export default {
  components: {
    PdlCollapse,
    PdlCollapseItem,
  },
  props: {
    product: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    /**
     * Currently two alt layouts dictated by the type.
     */
    type: {
      type: String,
      default: 'primary',
      validator: (value) => ['', 'primary', 'secondary'].includes(value),
    },
    /**
     * Can take a custom color option based on tailwind color classes.
     * Example: custom-color='red-90'
     */
    customColor: {
      type: String,
      default: '',
    },
    isModelSelectorCard: {
      type: Boolean,
      default: false,
    },
    isShowingAllSpecs: {
      type: Boolean,
      default: true,
    },
    qaidLabel: {
      type: String,
      default: 'spec-details',
    },
  },
  data() {
    return {
      Types: {
        PRIMARY: 'primary',
        SECONDARY: 'secondary',
      },
    };
  },
  computed: {
    ...mapState('productDetails', ['shortSpecsToShow']),
    ...mapState('familyDetails', ['areModelOptionSpecsActive']),
    ...mapGetters('familyDetails', ['familyShortSpecsToHighlight']),
    specsTitle() {
      return this.areModelOptionSpecsActive ? this.$t('text.seeLess') : this.$t('text.seeMore');
    },
    isSecondary() {
      return this.type === this.Types.SECONDARY && !this.isCustomColor;
    },
    isPrimary() {
      return this.type === this.Types.PRIMARY && !this.isCustomColor;
    },
    isCustomColor() {
      return !!this.customColor;
    },
    listItemClasses() {
      return {
        'mb-1': !this.isModelSelectorCard,
        'text-black': this.isPrimary,
        'text-gray-80': this.isSecondary,
        [`text-${this.customColor}`]: this.isCustomColor,
      };
    },
    specsToHighlight() {
      const shortSpecs = this.familyShortSpecsToHighlight || [];
      return Object.fromEntries(
        shortSpecs.filter((key) => key in this.shortSpecs).map((key) => [key, this.shortSpecs[key]])
      );
    },
    nonHighlightedSpecs() {
      const specsToHighlight = this.familyShortSpecsToHighlight || [];

      return Object.fromEntries(
        Object.entries(this.shortSpecs).filter(
          ([key]) => !specsToHighlight.includes(key) && this.shortSpecsToShow.includes(key)
        )
      );
    },
    hasNonHighlightedSpecs() {
      return Object.keys(this.nonHighlightedSpecs).length > 0;
    },
    shortSpecs() {
      // Filters specs to return only those with keys containing "shortSpec"
      return Object.fromEntries(Object.entries(this.product?.specs || {}).filter(([key]) => key.includes('shortSpec')));
    },
  },
  watch: {
    areModelOptionSpecsActive(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.$refs['specs-collapse'].setActiveNames(['model-specs']);
        });
      } else {
        this.$nextTick(() => {
          this.$refs['specs-collapse'].setActiveNames([]);
        });
      }
    },
  },
  methods: {
    ...mapMutations('familyDetails', ['setAreModelOptionSpecsActive']),
    collapseChange() {
      this.setAreModelOptionSpecsActive(!this.areModelOptionSpecsActive);
    },
  },
};
</script>

<style lang="scss" scoped>
.spec-name::after {
  content: '/';
  margin-inline: 0.25rem;
}

.pdl-collapse {
  &:deep(.pdl-collapse-item__header),
  &:deep(.pdl-collapse-item__wrap) {
    background: transparent;
    border-bottom: 1px solid transparent;
  }
}
</style>
